import {TimeCardDataSourceType} from "../../types/timecard-data-source.type";

// Data-level risks (per TimeCardData)
export enum TimeCardDataRisk {
  DistanceDeviationThresholdExceeded = "DistanceDeviationThresholdExceeded",
  ServiceTimeDeviationThresholdExceeded = "ServiceTimeDeviationThresholdExceeded",
  DriveTimeDeviationThresholdExceeded = "DriveTimeDeviationThresholdExceeded",
  PaidTimeExceedsShiftDuration = "PaidTimeExceedsShiftDuration",
  PaidTimeMismatch = "PaidTimeMismatch",
  ActualMillageEntryWithoutReportedTasks = "ActualMillageEntryWithoutReportedTasks",
  ReportedTaskWithoutActualMillageEntries = "ReportedTaskWithoutActualMillageEntries"
}

// Entry-level risks (per TimeCardEntry)
export enum TimeCardEntryRisk {
  // Reported risks
  DepartureBeforeArrival = "DepartureBeforeArrival",
  MissingArrivalWithExistingDeparture = "MissingArrivalWithExistingDeparture",
  MissingDepartureWithExistingArrival = "MissingDepartureWithExistingArrival",
  ZeroOrNegativeServiceTime = "ZeroOrNegativeServiceTime",
  OverlappingStops = "OverlappingStops",
  NegativeDriveTime = "NegativeDriveTime",
  DriveTimeThresholdExceeded = "DriveTimeThresholdExceeded",
  DistanceWithZeroDriveTime = "DistanceWithZeroDriveTime",
  DriveTimeWithZeroDistance = "DriveTimeWithZeroDistance",
  // Planned vs Reported risks
  PlannedStopNotReported = "PlannedStopNotReported",
  ReportedStopNotPlanned = "ReportedStopNotPlanned",
  // Reported (Initial vs Adjusted) risks
  AdjustedStartTimeDeviationThresholdExceeded = "AdjustedStartTimeDeviationThresholdExceeded",
  AdjustedEndTimeDeviationThresholdExceeded = "AdjustedEndTimeDeviationThresholdExceeded",
  AdjustedDistanceDeviationThresholdExceeded = "AdjustedDistanceDeviationThresholdExceeded",
  // Reported vs Geo risks
  ArrivalTimeMismatch = "ArrivalTimeMismatch",
  DepartureTimeMismatch = "DepartureTimeMismatch",
  ReportedStopWithoutGeoEvents = "ReportedStopWithoutGeoEvents",
  // Geo Tracking risks
  ExcessiveDwellTime = "ExcessiveDwellTime",
  // Mileage vs Geo risks
  MileageWithoutGeoContinuity = "MileageWithoutGeoContinuity"
}

// Entry risks that mark the timecard as infeasible (per spec)
export const INFEASIBLE_ENTRY_RISKS: Set<string> = new Set([
  TimeCardEntryRisk.DepartureBeforeArrival,
  TimeCardEntryRisk.MissingArrivalWithExistingDeparture,
  TimeCardEntryRisk.MissingDepartureWithExistingArrival,
  TimeCardEntryRisk.OverlappingStops,
  TimeCardEntryRisk.NegativeDriveTime
]);

// Entry risks shown only as GPS cell colouring, without the warning icon/badge
export const GEO_TIME_MISMATCH_ENTRY_RISKS: Set<string> = new Set([
  TimeCardEntryRisk.ArrivalTimeMismatch,
  TimeCardEntryRisk.DepartureTimeMismatch
]);

export interface TimeCardEntryAnalysis {
  id: string;
  risks: TimeCardEntryRisk[];
}

// Entry analysis with computed flags, as used for display
export interface TimeCardEntryAnalysisView {
  risks: TimeCardEntryRisk[];
  hasInfeasibleRisk: boolean;
  hasWarningRisk: boolean;
  hasArrivalTimeMismatch: boolean;
  hasDepartureTimeMismatch: boolean;
}

export interface TimeCardDataAnalysis {
  id: string;
  source: TimeCardDataSourceType;
  entries: TimeCardEntryAnalysis[];
  risks: TimeCardDataRisk[];
  hasInfeasibleRisk: boolean;
  hasWarningRisk: boolean;
}

export interface TimeCardAnalysis {
  timeCardId: string;
  reportedData?: TimeCardDataAnalysis;
  plannedData?: TimeCardDataAnalysis;
  actualTrackingData?: TimeCardDataAnalysis;
  geoEventTrackingData?: TimeCardDataAnalysis;
  hasInfeasibleRisk: boolean;
  hasWarningRisk: boolean;
}
